<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full gap-2 items-center">
                <span class="flex-grow"></span>
                <a
                    :href="lastInvoicesExport ? lastInvoicesExport.url : '#'"
                    ref="lastPdfLink"
                    download
                    target="_blank"
                    class="underline text-xs"
                    v-if="lastInvoicesExport && lastInvoicesExport.generationSucceeded"
                    :title="$t('commons.lastExport') + ' (' + humanizeDate(lastInvoicesExport.createdAt) + ')'"
                >
                    {{ $t('commons.lastExport') }}
                </a>
                <app-button
                    size="mini"
                    class="p-2 shadow"
                    v-click-helper="openPdf"
                    :aria-label="$t('commons.toPdf')"
                    :title="$t('commons.toPdf')"
                    :disabled="lastInvoicesExport && !lastInvoicesExport.generationEndDate"
                    :icon="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate
                            ? 'icon-rotate-right'
                            : 'icon-file-pdf-outline'
                    "
                    :icon-class="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate ? 'animate animate-spin' : ''
                    "
                />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th colspan="2"></th>
                        <th colspan="8" class="border bg-yellow-500">{{ $t('amendments.title') }}</th>
                        <th colspan="8" class="border bg-red-300">{{ $t('invoices.title') }}</th>
                    </tr>
                    <tr>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            {{ $t('commons.bundle') }}
                        </th>
                        <th class="text-left border-r p-1">
                            {{ $t('invoices.mainCompany') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.market') }}
                        </th>
                        <th class="text-center border-r p-1">
                            <div :title="$t('statements.allAmendmentsTips')">
                                {{ $t('statements.allAmendments') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.marketPlusAmendments') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.caution') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.deductions') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.cie') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.penalties') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.total') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.situationMainCompany') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.situationSubcontractors') }}
                        </th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationTotal') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationGaranteeDeduction') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationCIE') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationPenalties') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationOtherDeductions') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-right border-r p-1" colspan="2">
                            <i>{{ $t('statements.allBundles') }}</i>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(totalMarket, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalMarketPlusAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalCaution, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalDeductions, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalCie, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(grantTotal, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationTotalMainCompany, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationTotalSubcontractors, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(totalSituationTotal, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationGaranteeDeduction, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">{{ $n(totalSituationCIE, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">{{ $n(totalSituationPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationOtherDeductions, currencyFormat) }}
                        </td>
                    </tr>

                    <tr v-if="filteredItems.length > 1 && filterValue.length > 0">
                        <td class="text-right border-r p-1" colspan="2">
                            <i>{{ $t('statements.allFilteredLines') }}</i>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalMarket, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredTotalMarketPlusAmendments, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalCaution, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalDeductions, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalCie, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredGrantTotal, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationTotalMainCompany, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationTotalSubcontractors, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredSituationTotal, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationGaranteeDeduction, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">{{ $n(filteredSituationCIE, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">{{ $n(filteredSituationPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationOtherDeductions, currencyFormat) }}
                        </td>
                    </tr>
                    <template v-for="invoice in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="text-left border-r p-1">
                                {{ invoice.emitter.label }}
                            </td>
                            <td class="text-left border-r p-1">
                                {{ invoice.emitter.company.name }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalMarket, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalAmendments, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.totalMarketPlusAmendments, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalCaution, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalDeductions, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalCie, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(invoice.totalPenalties, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(invoice.grantTotal, currencyFormat) }}</td>

                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationTotalMainCompany, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationTotalSubcontractors, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationTotal, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationGaranteeDeduction, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(invoice.situationCIE, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationPenalties, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(invoice.situationOtherDeductions, currencyFormat) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
    </main>
</template>

<script>
import AppCheckbox from '../../components/app-checkbox/AppCheckbox';
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, groupBy, sortBy, uniqBy, reduceSumOn } from '@/services/sanitize.service';
import AppList from '../../components/appList/AppList';
import AppSelect from '@/components/appSelect/AppSelect';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import { getInvoices, getInvoiceStatus, getSituationStatus } from '@/features/invoices/invoices.service';
import AppPreparationVisaStatus from '@/components/appPreparationVisaStatus/AppPreparationVisaStatus';
import AppDateLink from '@/components/appDateLink/AppDateLink';
import AppPopup from '@/components/app-popup/AppPopup';
import AppBundlePicker from '@/components/appBundlePicker';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppFooter from '@/components/appFooter/AppFooter';
import AppInputText from '@/components/appInputText/AppInputText';
import AppCancel from '@/components/appCancel/AppCancel';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink';
import { queryProject } from '@/features/projects/projects.service';

import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import AppTips from '@/components/app-tips/AppTips.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import IconAlertCircle from '@/icons/IconAlertCircle.vue';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import { replicateProjects } from '@/rxdb/database';
import { error, info } from '@/features/toasts/toats.service';
import { createInvoicesExport, getLastInvoicesExport } from '@/features/invoices/invoicesExports.service';
import { humanizeDate } from '@/filters/dateFilter';
import vueClickHelper from 'vue-click-helper';
import { getInvoicesReviews } from '@/features/invoices/invoiceReviews.service';
import { getAmendments } from '@/features/amendments/amendments.service';
import {
    getTotalPenalties,
    getTotalAmendments,
    getTotalCaution,
    getTotalDeductions,
    getTotalMarket,
    getTotalMarketPlusAmendments,
    getTotalCie,
    getGrantTotal,
    getSituationMainCompany,
    getSituationSubcontractors,
    getSituationCIE,
    getSituationGaranteeDeduction,
    getSituationPenalties,
    getSituationOtherDeductions,
} from '@/features/statements/statements.service';
import { getDeductions } from '@/features/deductions/deductions.service';
import { getCIEs } from '@/features/cie/CIE.service';

export default {
    components: {
        IconAlertOutline,
        IconAlertCircle,
        AppLabel,
        AppTextarea,
        AppTips,
        AppNumberLink,
        AppNumberInput,
        IconMenuUp,
        IconMenuDown,
        AppCancel,
        AppInputText,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppDateLink,
        AppPreparationVisaStatus,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
        AppSelect,
        AppList,
        AppCheckbox,
    },
    directives: { 'click-helper': vueClickHelper },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotalMarket() {
            return reduceSumOn(this.filteredItems, 'totalMarket');
        },
        filteredTotalAmendments() {
            return reduceSumOn(this.filteredItems, 'totalAmendments');
        },
        filteredTotalMarketPlusAmendments() {
            return reduceSumOn(this.filteredItems, 'totalMarketPlusAmendments');
        },
        filteredTotalCaution() {
            return reduceSumOn(this.filteredItems, 'totalCaution');
        },
        filteredTotalDeductions() {
            return reduceSumOn(this.filteredItems, 'totalDeductions');
        },
        filteredTotalPenalties() {
            return reduceSumOn(this.filteredItems, 'totalPenalties');
        },
        filteredTotalCie() {
            return reduceSumOn(this.filteredItems, 'totalCie');
        },
        filteredGrantTotal() {
            return reduceSumOn(this.filteredItems, 'grantTotal');
        },
        filteredSituationTotalMainCompany() {
            return reduceSumOn(this.filteredItems, 'situationTotalMainCompany');
        },
        filteredSituationTotalSubcontractors() {
            return reduceSumOn(this.filteredItems, 'situationTotalSubcontractors');
        },
        filteredSituationTotal() {
            return reduceSumOn(this.filteredItems, 'situationTotal');
        },
        filteredSituationGaranteeDeduction() {
            return reduceSumOn(this.filteredItems, 'situationGaranteeDeduction');
        },
        filteredSituationCIE() {
            return reduceSumOn(this.filteredItems, 'situationCIE');
        },
        filteredSituationPenalties() {
            return reduceSumOn(this.filteredItems, 'situationPenalties');
        },
        filteredSituationOtherDeductions() {
            return reduceSumOn(this.filteredItems, 'situationOtherDeductions');
        },
        filteredItems() {
            let result = uniqBy(this.filterFn(this.filterValue), 'groupId').map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    const text = item.code ? item.code + ' ' + item.object : item.object;
                    if (this.sortKey === 'object') {
                        return text;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate + ' ' + text;
                    } else if (this.sortKey === 'emitter') {
                        return (item.bundle ? item.bundle.label : null) + text;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            id: 'invoice_toEmit',
                            name: this.$t('invoices.status.toEmit'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_toValidate',
                            name: this.$t('invoices.status.toValidate'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_validated',
                            name: this.$t('invoices.status.validated'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_rejected',
                            name: this.$t('invoices.status.rejected'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_obsolete',
                            name: this.$t('invoices.status.obsolete'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        humanizeDate,
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoicesReviews(this.$route.params.projectId),
                    getAmendments(this.$route.params.projectId),
                    getDeductions(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, invoices, companies, invoiceReviews, amendments, deductions, cies]) => {
                    this.amendments = uniqBy(amendments, 'groupId').filter(
                        (amendment) => amendment.quoteStatus !== 'rejected' && amendment.quoteStatus !== 'obsolete',
                    );
                    const relevantDeductions = deductions.filter(
                        (deduction) => deduction.deductionStatus && deduction.deductionStatus !== 'obsolete',
                    );
                    const relevantCIEs = cies.filter(
                        (cie) => cie.quoteStatus !== 'rejected' && cie.quoteStatus !== 'obsolete',
                    );
                    this.totalMarket = getTotalMarket(this.amendments);
                    this.totalAmendments = getTotalAmendments(this.amendments);
                    this.totalMarketPlusAmendments = getTotalMarketPlusAmendments(this.amendments);
                    this.totalCaution = getTotalCaution(relevantDeductions);
                    this.totalDeductions = getTotalDeductions(relevantDeductions);
                    this.totalPenalties = getTotalPenalties(relevantDeductions);
                    this.totalCie = getTotalCie(cies);
                    this.grantTotal = getGrantTotal(this.amendments, deductions, cies, 0);
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);

                    this.items = groupBy(
                        uniqBy(invoices, 'groupId').map((invoice) => {
                            const emitter = this.bundleMap[invoice.emitterId];
                            const invoiceStatus = getInvoiceStatus(
                                invoice,
                                invoiceReviews.filter((invoiceReview) => invoiceReview.invoiceId === invoice.id),
                            );
                            const situationStatus = getSituationStatus(
                                invoice,
                                invoiceReviews.filter((invoiceReview) => invoiceReview.invoiceId === invoice.id),
                            );
                            return {
                                ...invoice,
                                index: invoice.versions.length,
                                versions: invoice.versions.map((version) => ({
                                    ...version,
                                    emitter: this.bundleMap[version.bundleId],
                                })),
                                emitter,
                                filterString: [
                                    invoice.code,
                                    invoice.object,
                                    invoice.emitterId && emitter ? emitter.label : '',
                                ].join(),
                                invoiceStatus,
                                situationStatus,
                            };
                        }),
                        'emitterId',
                    ).map((group) => {
                        const emitterId = group[0].emitterId;
                        const amendments = this.amendments.filter((amendment) => amendment.bundleId === emitterId);
                        const bundleCies = relevantCIEs.filter((cie) => cie.bundleId === emitterId);
                        const totalCIEResponsability = relevantCIEs.reduce(
                            (acc, cie) =>
                                acc +
                                reduceSumOn(
                                    cie.distribution.filter((distribution) => distribution.bundleId === emitterId),
                                    'amount',
                                ),
                            0,
                        );
                        const situationTotalMainCompany = getSituationMainCompany(group, emitterId);
                        const situationTotalSubcontractors = getSituationSubcontractors(group, emitterId);
                        const situationCIE = getSituationCIE(group);
                        const situationGaranteeDeduction = getSituationGaranteeDeduction(group);
                        const situationPenalties = getSituationPenalties(group);
                        const situationOtherDeductions = getSituationOtherDeductions(group);
                        return {
                            ...group[0],
                            relatedInvoices: group,
                            totalMarket: getTotalMarket(amendments),
                            totalAmendments: getTotalAmendments(amendments),
                            totalMarketPlusAmendments: getTotalMarketPlusAmendments(amendments),
                            totalCaution: getTotalCaution(amendments),
                            totalDeductions: getTotalDeductions(amendments),
                            totalPenalties: getTotalPenalties(amendments),
                            totalCie: getTotalCie(bundleCies) + totalCIEResponsability,
                            grantTotal: getGrantTotal(amendments, deductions, bundleCies, totalCIEResponsability),
                            situationTotalMainCompany,
                            situationTotalSubcontractors,
                            situationTotal: situationTotalSubcontractors + situationTotalMainCompany,
                            situationGaranteeDeduction,
                            situationCIE,
                            situationPenalties,
                            situationOtherDeductions,
                        };
                    });

                    this.totalSituationTotalMainCompany = reduceSumOn(this.items, 'situationTotalMainCompany');
                    this.totalSituationTotalSubcontractors = reduceSumOn(this.items, 'situationTotalSubcontractors');
                    this.totalSituationTotal = reduceSumOn(this.items, 'situationTotal');
                    this.totalSituationGaranteeDeduction = reduceSumOn(this.items, 'situationGaranteeDeduction');
                    this.totalSituationCIE = reduceSumOn(this.items, 'situationCIE');
                    this.totalSituationPenalties = reduceSumOn(this.items, 'situationPenalties');
                    this.totalSituationOtherDeductions = reduceSumOn(this.items, 'situationOtherDeductions');

                    this.scrollToLastVisited();
                    this.lastInvoicesExport = await getLastInvoicesExport(this.$route.params.projectId);
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedInvoiceId = localStorage.getItem(
                'invoice.lastVisitedInvoiceId.' + this.$route.params.projectId,
            );
            if (lastVisitedInvoiceId) {
                setTimeout(() => {
                    const element = this.$el.querySelector
                        ? this.$el.querySelector('#uuid_' + lastVisitedInvoiceId)
                        : null;
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        matchBundleFilter(item, bundleIds) {
            return item.emitter && bundleIds.includes(item.emitter.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) => item.invoiceStatus && criteria.id === 'invoice_' + item.invoiceStatus,
            );
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'statements_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('statements_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        openPdf(e, isDoubleClick) {
            if (isDoubleClick) {
                this.$refs.lastPdfLink.click();
            } else {
                this.exportPDF();
            }
        },
        async exportPDF() {
            await replicateProjects(this.$route.params.projectId);
            this.lastInvoicesExport = { generationEndDate: null };
            const lastInvoicesExport = await createInvoicesExport(this.$route.params.projectId, {
                sortKey: this.sortKey,
                sortAsc: this.sortAsc,
                filter: this.filterValue,
            });
            if (lastInvoicesExport) {
                if (lastInvoicesExport.generationSucceeded === true) {
                    info(
                        this.$t('commons.generationSucceed') +
                            `<br/><a href="${lastInvoicesExport.url}"><u>Télécharger le pdf</u></a>`,
                        5000,
                    );
                } else if (lastInvoicesExport.generationSucceeded === false) {
                    error(this.$t('commons.generationFailed'));
                }
            }
            this.lastInvoicesExport = lastInvoicesExport;
        },
    },
    data() {
        return {
            lastInvoicesExport: null,
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [],
            sortKey: 'object',
            sortAsc: true,
            selection: [],
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
            amendments: [],
            totalMarket: 0,
            totalAmendments: 0,
            totalMarketPlusAmendments: 0,
            totalCaution: 0,
            totalDeductions: 0,
            totalPenalties: 0,
            totalCie: 0,
            grantTotal: 0,

            totalSituationTotalMainCompany: 0,
            totalSituationTotalSubcontractors: 0,
            totalSituationTotal: 0,
            totalSituationGaranteeDeduction: 0,
            totalSituationCIE: 0,
            totalSituationPenalties: 0,
            totalSituationOtherDeductions: 0,
        };
    },
};
</script>
